import type { ComponentType } from "react";
import type { Article, ArticleFrontmatter, Language } from "@/types/blog";

interface CompiledMdxModule {
  frontmatter: ArticleFrontmatter;
  default: ComponentType;
}

const WORDS_PER_MINUTE = 225;

const compiledModules = import.meta.glob<CompiledMdxModule>(
  "/src/content/blog/*.mdx",
  { eager: true },
);

// The MDX plugin runs at `enforce: 'pre'` and may intercept .mdx?raw
// requests before Vite's raw handler, so values here can be strings,
// module objects, or undefined depending on plugin order. The function
// below handles all three.
const rawModules = import.meta.glob(
  "/src/content/blog/*.mdx",
  { eager: true, query: "?raw", import: "default" },
);

function estimateReadingTime(source: unknown): number {
  if (typeof source !== "string" || source.length === 0) return 1;

  const stripped = source
    // Strip the explicit frontmatter export block (multiline, non-greedy).
    .replace(/export\s+const\s+frontmatter\s*=\s*\{[\s\S]*?\};?/g, " ")
    // Strip YAML frontmatter at the top of the file, if present.
    .replace(/^---[\s\S]*?---/m, " ")
    // Strip fenced code blocks.
    .replace(/```[\s\S]*?```/g, " ")
    // Strip inline JSX/HTML tags.
    .replace(/<[^>]+>/g, " ")
    // Strip markdown punctuation noise.
    .replace(/[#*_>`~\-\[\]\(\)!]/g, " ")
    // Collapse remaining non-word/non-whitespace into spaces.
    .replace(/[^\w\s]/g, " ");

  const words = stripped.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}

const ARTICLES: Article[] = Object.entries(compiledModules)
  .map(([filePath, mod]) => {
    const fm = mod.frontmatter;
    if (!fm || !fm.slug) return null;
    return {
      frontmatter: fm,
      Component: mod.default,
      readingTime:
        typeof fm.readingTime === "number" && fm.readingTime > 0
          ? fm.readingTime
          : estimateReadingTime(rawModules[filePath]),
    } satisfies Article;
  })
  .filter((a): a is Article => a !== null)
  .sort((a, b) =>
    b.frontmatter.publishedAt.localeCompare(a.frontmatter.publishedAt),
  );

export function getAllArticles(language?: Language): Article[] {
  return language
    ? ARTICLES.filter((a) => a.frontmatter.language === language)
    : ARTICLES;
}

export function getArticleBySlug(slug: string): Article | null {
  return ARTICLES.find((a) => a.frontmatter.slug === slug) ?? null;
}

export function getFeaturedArticle(language?: Language): Article | null {
  const pool = getAllArticles(language);
  return pool.find((a) => a.frontmatter.featured === true) ?? null;
}

export function getArticlesByTag(tag: string, language?: Language): Article[] {
  return getAllArticles(language).filter((a) => a.frontmatter.tags.includes(tag));
}

export function getAllTags(language?: Language): string[] {
  const set = new Set<string>();
  for (const a of getAllArticles(language)) {
    for (const t of a.frontmatter.tags) set.add(t);
  }
  return [...set].sort();
}

export function getRelatedArticles(slug: string, limit = 3): Article[] {
  const current = getArticleBySlug(slug);
  if (!current) return [];

  const others = ARTICLES.filter((a) => a.frontmatter.slug !== slug);
  const explicit = current.frontmatter.relatedSlugs ?? [];

  const explicitMatches = explicit
    .map((s) => others.find((a) => a.frontmatter.slug === s))
    .filter((a): a is Article => Boolean(a));

  if (explicitMatches.length >= limit) return explicitMatches.slice(0, limit);

  const seen = new Set(explicitMatches.map((a) => a.frontmatter.slug));
  const sharedTagMatches = others
    .filter((a) => !seen.has(a.frontmatter.slug))
    .map((a) => {
      const overlap = a.frontmatter.tags.filter((t) =>
        current.frontmatter.tags.includes(t),
      ).length;
      return { article: a, overlap };
    })
    .filter((x) => x.overlap > 0)
    .sort((a, b) => b.overlap - a.overlap)
    .map((x) => x.article);

  return [...explicitMatches, ...sharedTagMatches].slice(0, limit);
}
